import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type PagedResult, type StructureListItem } from '../lib/api';
import { structureListDisplayName } from '../lib/structureReport';
import { useAuth } from '../lib/auth';

/**
 * Structure list — mirrors `.old/resources/views/structure/index.blade.php`.
 *
 * Legacy renders two separate tables: a "Draft Structures" section (structures
 * the user closed/kept as a draft but has not yet added to a project, shared,
 * or saved) above the main "Structures" section. We preserve that split so a
 * freshly-closed draft lands in its own clearly-labelled area.
 */
export function StructuresListPage() {
  const { user } = useAuth();
  const isSuper = !!user?.roles.includes('super-admin');
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);

  const params = new URLSearchParams({ pageSize: '50' });
  if (search.trim()) params.set('search', search.trim());
  if (isSuper && showAll) params.set('all', 'true');

  const { data, isLoading, error } = useQuery({
    queryKey: ['structures', { search: search.trim(), all: showAll }],
    queryFn: () => api.get<PagedResult<StructureListItem>>(`/api/v1/structures?${params}`),
  });

  const drafts = data?.items.filter((s) => s.isDraft) ?? [];
  const structures = data?.items.filter((s) => !s.isDraft) ?? [];

  return (
    <div className="container-fluid">
      <div className="row mt-3">
        <div className="col-12">
          <div className="page-title-box d-flex justify-content-between align-items-center flex-wrap gap-2">
            <h4 className="page-title mb-0">Structures</h4>
            <Link to="/newstructure" className="btn btn-primary">
              + New Structure
            </Link>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12 d-flex flex-wrap justify-content-end align-items-center gap-3 mb-2">
          {isSuper && (
            <div className="form-check mb-0">
              <input
                type="checkbox"
                className="form-check-input"
                id="str-show-all"
                checked={showAll}
                onChange={(e) => setShowAll(e.target.checked)}
              />
              <label className="form-check-label small" htmlFor="str-show-all">
                All users
              </label>
            </div>
          )}
          <input
            type="search"
            className="form-control form-control-sm"
            style={{ width: 220 }}
            placeholder="Search structures…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {isLoading && (
        <div className="row">
          <div className="col-12">
            <p className="text-muted">Loading…</p>
          </div>
        </div>
      )}
      {error && (
        <div className="row">
          <div className="col-12">
            <p className="text-danger">Failed to load structures.</p>
          </div>
        </div>
      )}

      {data && drafts.length > 0 && (
        <StructureSection title="Draft Structures" items={drafts} emptyText="No draft structures." />
      )}

      {data && (
        <StructureSection title="Structures" items={structures} emptyText="No structures." />
      )}
    </div>
  );
}

function StructureSection({
  title,
  items,
  emptyText,
}: {
  title: string;
  items: StructureListItem[];
  emptyText: string;
}) {
  return (
    <div className="row">
      <div className="col-12">
        <div className="card">
          <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
            <h4 className="header-title mb-0">
              {title}
              <span className="text-muted small ms-2">· {items.length}</span>
            </h4>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-striped table-hover mb-0">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Nickname</th>
                    <th>Type</th>
                    <th>Style Number</th>
                    <th>Casting Used</th>
                    <th>Foundry</th>
                    <th>Status</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 && (
                    <tr>
                      <td colSpan={8} className="text-center text-muted py-3">
                        {emptyText}
                      </td>
                    </tr>
                  )}
                  {items.map((s) => (
                    <tr key={s.id}>
                      <td>
                        <Link to={`/structurereport/view/${s.id}`}>{s.id}</Link>
                      </td>
                      <td>{s.nickname ?? <span className="text-muted">{structureListDisplayName(s)}</span>}</td>
                      <td>{s.structureTypeName ?? '—'}</td>
                      <td>{s.styleNumber ?? '—'}</td>
                      <td>{s.foundryPartNumber ?? '—'}</td>
                      <td>{s.foundryName ?? '—'}</td>
                      <td>
                        {s.isDraft ? (
                          <span className="badge bg-warning">Draft</span>
                        ) : s.isCompleted ? (
                          <span className="badge bg-success">Complete</span>
                        ) : (
                          <span className="badge bg-secondary">Open</span>
                        )}
                      </td>
                      <td>{formatDate(s.updatedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function formatDate(iso: string | null) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}
